// src/pages/bluebi/BlueBi.tsx
// Grid de painéis do módulo Blue B.I. (issue #42). Molde visual e de estados
// (carregando / erro / vazio) herdado de `pages/connection/Integracoes.tsx`.
//
// ⚠️ CONTENÇÃO DO LINK (issue #42 §1, ADR-056 do backend): `painel.link` é uma URL
// "Publish to web" anônima, pública e permanente, que a aplicação não consegue revogar.
// Por isso, nesta tela:
//   - o link só existe dentro do `src` do iframe — nunca em `logger.*`, nunca em
//     mensagem de erro, nunca na barra de endereços;
//   - a navegação usa o SLUG do rótulo (`/bluebi/<slug>`), não o link;
//   - as mensagens de erro são próprias, não ecoam o corpo devolvido pela API.
import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartSimple, faPen } from '@/icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlement } from '../../contexts/EntitlementContext';
import { apiGet } from '../../services/api';
import { logger } from '@/utils/logger';
import Modal from '../../components/common/Modal';
import IconFolderPlusButton from '../../components/common/IconFolderPlusButton';
import { Toast } from '../../contexts/ToastContext';
import { useBlueBiMutations } from '../../hooks/useBlueBiMutations';
import PainelFormModalContent from './PainelFormModalContent';
import { PainelGestao, painelPodeAbrir, slugDoRotulo } from '../../types/bluebi/Painel';
import './BlueBi.css';

const BlueBi = () => {
  const { theme } = useTheme();
  const { hasPermission } = useAuth();
  const { isAreaEnabled } = useEntitlement();
  const [paineis, setPaineis] = useState<PainelGestao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  // Presente = o modal está editando este painel; ausente com modal aberto = cadastro.
  const [painelEmEdicao, setPainelEmEdicao] = useState<PainelGestao | null>(null);

  // Quem pode editar lê a rota de GESTÃO — a única que devolve também os painéis
  // desativados, e por isso a única pela qual eles podem voltar a circular. Quem só
  // visualiza lê a rota de consumo, que devolve os ativos aos quais está vinculado
  // (ROLE_MASTER recebe todos os ativos, sem depender de vínculo — ADR-059).
  const podeEditar = hasPermission('bluebi.editar');
  // Gate ANTES do request, como a ADR-027 (item 5) fixou para a Home: sem o eixo de
  // permissão e o de módulo, a chamada não sai. O early-return abaixo acontece no render,
  // mas o efeito de carga já teria sido agendado no mount — e o redirect do ProtectedRoute
  // também é calculado em efeito, então a página monta por um instante. Sem esta guarda,
  // quem não tem `bluebi.visualizar` tomaria um 400 numa tela que vai desaparecer.
  const podeCarregar = hasPermission('modulo.bluebi') && isAreaEnabled('bluebi') && (podeEditar || hasPermission('bluebi.visualizar'));

  const carregarPaineis = useCallback(async () => {
    if (!podeCarregar) {
      setCarregando(false);
      return;
    }
    setCarregando(true);
    setErro(null);
    try {
      const rota = podeEditar ? '/v1/bluebi/gestao/paineis' : '/v1/bluebi/paineis';
      const response = await apiGet(rota);
      setPaineis(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      // Loga só a mensagem — nunca o erro Axios cru, que carrega `config` (com o
      // Bearer) e o corpo da resposta. Mesmo cuidado do EntitlementContext.
      logger.error('Falha ao carregar painéis do Blue B.I.:', err instanceof Error ? err.message : String(err));
      // Mensagem PRÓPRIA: o corpo da API não é ecoado, para nenhum caminho de erro
      // conseguir vazar um link por acidente.
      setErro('Não foi possível carregar os painéis. Tente novamente.');
    } finally {
      setCarregando(false);
    }
  }, [podeEditar, podeCarregar]);

  useEffect(() => {
    document.title = 'Bluelt | Blue B.I.';
  }, []);

  useEffect(() => {
    carregarPaineis();
  }, [carregarPaineis]);

  const { isSaving, criarPainel, atualizarPainel, alternarAtivo } = useBlueBiMutations({
    onReload: carregarPaineis,
  });

  const fecharModal = () => {
    setModalAberto(false);
    setPainelEmEdicao(null);
  };

  // Fecha o modal só quando a gravação deu certo; em recusa de validação ele fica aberto
  // com o que foi digitado, para o usuário corrigir em vez de redigitar tudo.
  const handleSalvarPainel = async (dados: { rotulo: string; link: string; usuarioIds: number[] }) => {
    const sucesso = painelEmEdicao
      ? await atualizarPainel(painelEmEdicao.id, dados)
      : await criarPainel(dados);
    if (sucesso) fecharModal();
    return sucesso;
  };

  const handleToggleAtivo = async (ativar: boolean) => {
    if (!painelEmEdicao) return false;
    const sucesso = await alternarAtivo(painelEmEdicao.id, ativar);
    if (sucesso) fecharModal();
    return sucesso;
  };

  const abrirEdicao = (painel: PainelGestao) => {
    setPainelEmEdicao(painel);
    setModalAberto(true);
  };

  // Abre o painel em aba nova. `window.open` sem `noopener` é DELIBERADO e necessário:
  // o JWT vive em `sessionStorage` (premissa #6), que só é herdado por aba aberta assim.
  // Com `noopener` — e também com `<a target="_blank">`, que o Chrome trata como noopener
  // desde a v88 — a aba nasceria sem sessão e cairia no /login. Verificado em sandbox
  // (fase 0, V2). Sem risco de reverse tabnabbing: o destino é a própria origem.
  //
  // Painel fora de circulação não abre, e o clique explica por quê em vez de não fazer
  // nada — o card continua na tela (é por ele que se reativa), então silêncio pareceria
  // defeito. A recusa não vive só aqui: `/bluebi/<slug>` refaz a checagem, senão a URL
  // digitada à mão contornaria esta linha.
  const abrirPainel = (painel: PainelGestao) => {
    if (!painelPodeAbrir(painel)) {
      Toast.info(`O painel ${painel.rotulo} está desativado. Reative-o para abrir.`);
      return;
    }
    window.open(`/bluebi/${slugDoRotulo(painel.rotulo)}`, '_blank');
  };

  if (!hasPermission('modulo.bluebi')) {
    return <div>Você não tem permissão para acessar esta página.</div>;
  }

  return (
    <div className={`bluebi-page ${theme}`}>
      <main className="bluebi-main">
        <h1>
          <FontAwesomeIcon icon={faChartSimple} /> Blue B.I.
        </h1>
        {podeEditar && (
          <div className="header-actions">
            <IconFolderPlusButton onClick={() => setModalAberto(true)} title="Novo painel" />
          </div>
        )}
      </main>
      {carregando && <p>Carregando...</p>}
      {erro && <p className="error-message">{erro}</p>}
      {!carregando && !erro && paineis.length === 0 && (
        <p>Nenhum painel disponível para você no momento.</p>
      )}
      {!carregando && !erro && paineis.length > 0 && (
        <div className="bluebi-cards-wrapper">
          <div className="bluebi-cards-container">
            {paineis.map((painel) => (
              <div
                key={painel.id}
                className={`bluebi-card ${painel.ativo === false ? 'invalid-card' : ''}`}
                onClick={() => abrirPainel(painel)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && abrirPainel(painel)}
                title={
                  painelPodeAbrir(painel)
                    ? `Abrir ${painel.rotulo}`
                    : `${painel.rotulo} está desativado e não pode ser aberto`
                }
              >
                <div className="bluebi-card-previa">
                  {/* Miniatura: o Power B.I. renderiza em tamanho fixo e um iframe pequeno
                      mostraria só o canto superior esquerdo do relatório. A prévia real vem
                      de renderizar em tamanho útil e reduzir por `transform: scale`, com o
                      container recortando (`overflow: hidden`). `pointer-events: none` para
                      o clique pertencer ao card, e `loading="lazy"` para o grid não disparar
                      um relatório inteiro por card de uma vez. Verificado na fase 0 (V3b). */}
                  <iframe
                    src={painel.link}
                    title={`Prévia do painel ${painel.rotulo}`}
                    className="bluebi-card-iframe"
                    loading="lazy"
                    tabIndex={-1}
                  />
                </div>
                <div className="bluebi-card-rodape">
                  <span className="bluebi-card-nome">{painel.rotulo}</span>
                  {painel.ativo === false && <span className="bluebi-card-tag">desativado</span>}
                  {podeEditar && (
                    <button
                      type="button"
                      className="edit-button bluebi-card-editar"
                      title={`Editar ${painel.rotulo}`}
                      // O card inteiro abre o relatório; o botão precisa reter o clique,
                      // senão editar também abriria a aba do painel.
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirEdicao(painel);
                      }}
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* O conteúdo só monta com o modal aberto (o Modal devolve `null` fechado), então
          fechar no "x" ou salvar desmonta o formulário — ele nasce limpo na próxima vez. */}
      <Modal isOpen={modalAberto} onClose={fecharModal} theme={theme}>
        <PainelFormModalContent
          // Remonta ao trocar de painel (ou entre cadastro e edição), para os campos não
          // carregarem o estado da abertura anterior.
          key={painelEmEdicao?.id ?? 'novo'}
          theme={theme}
          isLoading={isSaving}
          painel={painelEmEdicao ?? undefined}
          // O próprio painel fica fora da checagem de nome repetido — senão editar
          // qualquer outro campo esbarraria no nome que já é dele.
          rotulosExistentes={paineis
            .filter((p) => p.id !== painelEmEdicao?.id)
            .map((p) => p.rotulo)}
          onSave={handleSalvarPainel}
          onToggleAtivo={handleToggleAtivo}
        />
      </Modal>
    </div>
  );
};

export default BlueBi;
